// src/components/frontpage/columns/GroupsColumn.jsx
// Groups column for the Locality front page (desktop MultiViewGrid + mobile tab bar) —
// replaces the coming-soon Marketplace slot (July 2026). A doorway, not a replacement for
// discovery: shows a bounded 8-12 cards per mode (My/Owned/Discover) with a "See all groups"
// action that hands off to the existing GroupsGrid page. Locality-only — GroupFrontPage and
// MobileGroupFrontPage (already inside a selected group) are untouched; their Marketplace
// slot stays coming-soon.
//
// Data + click-through logic come from the shared useGroups hook (also used by GroupsGrid)
// so the two surfaces cannot drift. Card click behaviour mirrors GroupsGrid exactly: Joined
// mode uses a direct select (already a member); Owned/Discover use the landing-page-first
// visit.

import React, { useState, useEffect } from 'react';
import { themes } from '../../../themes/index.js';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';
import useGroups from '../../../hooks/useGroups';
import InitialPlaceholder from '../../core/InitialPlaceholder';
import ModeColumn from './ModeColumn';
import MobileActionLine from '../../core/HeadersAndFooters/MobileActionLine';

const MAX_CARDS = 12;

const GroupCard = ({ group, onClick, showIntro, accentColor }) => {
  const introduction = group.description
    ? (group.description.length > 100 ? group.description.substring(0, 100) + '...' : group.description)
    : null;

  return (
    <div
      onClick={() => onClick(group)}
      style={{
        display: 'flex',
        gap: '8px',
        padding: '8px',
        marginBottom: '8px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; }}
    >
      <InitialPlaceholder
        name={group.name}
        image={group.logo_url || null}
        fontSize="1.1rem"
        color={accentColor}
        style={{ width: '48px', height: '48px', borderRadius: '6px' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '600',
          fontSize: '13px',
          color: '#1f2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {group.name}
        </div>
        {showIntro && introduction && (
          <div style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '11px',
            color: '#6b7280',
            marginTop: '2px',
            lineHeight: '1.35',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {introduction}
          </div>
        )}
      </div>
    </div>
  );
};

const GroupsColumn = ({ currentUser, sessionReady = false, onGroupSelect, hideColumnHeader = false, selectedArea = null }) => {
  // Deliberately NOT useTheme() — this column is mounted while the ambient ThemeProvider is
  // still 'locality' (it's the doorway INTO Groups, not inside it yet), so useTheme() would
  // return locality blue. The colour system is semantic here: mauve always means Groups, so
  // this column sources it directly rather than inheriting the surrounding context's colour.
  // Same reasoning for the wash: themes.groups.bg only applies ambient-wide once currentContext
  // has actually switched (e.g. GroupFrontPage) — this column needs it before that happens.
  const groupsColor = themes.groups.primary;
  const groupsBg = themes.groups.bg;
  const { t } = useTranslation('frontpage');
  const { t: tGroups } = useTranslation('groups');
  const { requireAuth, isLoggedIn } = useAuth();

  const {
    joinedGroups,
    ownedGroups,
    discoverGroups,
    loading,
    selectGroup,
    visitGroup
  } = useGroups({ currentUser, sessionReady, onGroupSelect, selectedArea });

  const [viewMode, setViewMode] = useState(isLoggedIn ? 'joined' : 'discover');
  const [modeInitialized, setModeInitialized] = useState(false);

  // Default mode, computed once data has loaded: logged in + has joined groups → Joined;
  // logged in + none → Discover; logged out → Discover. Only runs once per session so it
  // doesn't fight a user's own tab taps afterwards.
  useEffect(() => {
    if (modeInitialized || loading) return;
    if (!isLoggedIn) {
      setViewMode('discover');
    } else {
      setViewMode(joinedGroups.length > 0 ? 'joined' : 'discover');
    }
    setModeInitialized(true);
  }, [modeInitialized, loading, isLoggedIn, joinedGroups.length]);

  const handleCreateGroup = () => {
    requireAuth('gate_body_create_group', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'new-group' } }));
    });
  };

  const handleSeeAllGroups = () => {
    window.dispatchEvent(new CustomEvent('ournet:context-switch', { detail: { context: 'groups' } }));
  };

  const activeGroups = viewMode === 'joined' ? joinedGroups
    : viewMode === 'owned' ? ownedGroups
    : discoverGroups;

  const boundedGroups = activeGroups.slice(0, MAX_CARDS);
  const showLoading = !sessionReady || loading;
  const isEmpty = !showLoading && boundedGroups.length === 0;

  const cardClickHandler = viewMode === 'joined' ? selectGroup : visitGroup;

  // Pinned mobile sub-header — create + search, sibling above ModeColumn. Retires the old
  // shared HeaderButtonsFeed Row 3 for this tab (mobile-tab-subheaders brief). The existing
  // mode-selector pills below stay put — pre-existing, part of this column's own scrolling
  // body, not the retired shared row.
  const mobileSubHeader = (
    <MobileActionLine
      contextColor={groupsColor}
      createLabel={t('mobile_create_group')}
      onCreate={handleCreateGroup}
      topBorder={false}
      sideBorders={false}
      searchPlaceholder={t('ui:search_articles')}
      onSearch={(term) => window.dispatchEvent(new CustomEvent('ournet:search-articles', { detail: { searchTerm: term } }))}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {hideColumnHeader && mobileSubHeader}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ModeColumn
          title={t('column_groups')}
          themeColor={groupsColor}
          containerBg={groupsBg}
          containerBorder={`1px solid ${groupsColor}4D`}
          hideColumnHeader={hideColumnHeader}
          onAdd={handleCreateGroup}
          addLabel={t('new_group')}
          footer={
            <button
              onClick={handleSeeAllGroups}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                marginTop: '4px',
                background: 'none',
                border: 'none',
                borderTop: '1px solid #eee',
                color: groupsColor,
                fontFamily: 'OpenSauceSans, sans-serif',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              {t('see_all_groups')}
            </button>
          }
        >
          {/* Mode selector — three compact pills (column width, not GroupsGrid's full radio row) */}
          <div style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '8px'
          }}>
            {[
              { id: 'joined', label: tGroups('groups_ive_joined') },
              { id: 'owned', label: tGroups('groups_i_own') },
              { id: 'discover', label: tGroups('discover_groups') }
            ].map(mode => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                style={{
                  flex: 1,
                  padding: '5px 4px',
                  background: viewMode === mode.id ? groupsColor : '#f3f4f6',
                  color: viewMode === mode.id ? 'white' : '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontFamily: 'OpenSauceSans, sans-serif',
                  fontSize: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {mode.label}
              </button>
            ))}
          </div>

          {/* Welcome state — logged-out visitors only, Discover tab only: the copy is written
              about discovering groups ("explore what's here, or sign up to join in"), so it's
              off-topic under Joined/Owned. The Directory rail carries the permanent "About
              OurNet" counterpart (see DirectoryColumn), so this stays reachable after
              registration without needing show/hide logic here. */}
          {!isLoggedIn && viewMode === 'discover' && (
            <div style={{
              padding: '10px',
              marginBottom: '8px',
              background: '#F5F0F7',
              borderRadius: '8px',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '12px',
              color: '#4b5563',
              lineHeight: '1.4'
            }}>
              {t('groups_welcome_message')}
            </div>
          )}

          {/* Loading/empty/cards handled locally rather than via ModeColumn's isEmpty/emptyLabel —
              that mechanism swaps out the whole of `children`, which would take the mode
              selector and welcome banner above with it.
              See docs/briefs/2026-08-13-groups-column-empty-state-trap.md. */}
          {showLoading ? (
            <div style={{
              padding: '32px 16px',
              textAlign: 'center',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              {tGroups('loading_groups')}
            </div>
          ) : isEmpty ? (
            <div style={{
              padding: '32px 16px',
              textAlign: 'center',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              <div>{t(`groups_empty_${viewMode}`)}</div>
              {viewMode !== 'discover' && (
                // Bare <button>, not StandardButton variant="inline" (LAYOUT.md § Usage Rule —
                // StandardButton vs raw <button>): that variant's text colour is a fixed token
                // (tokens.colors.inlineText) with no per-instance override, so it can't take
                // groupsColor — it would render the wrong colour here, not this column's mauve
                // (see the useTheme() note above on why this column can't rely on ambient theme).
                <button
                  onClick={() => setViewMode('discover')}
                  style={{
                    marginTop: '8px',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: groupsColor,
                    textDecoration: 'underline',
                    fontFamily: 'OpenSauceSans, sans-serif',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {tGroups('discover_groups')}
                </button>
              )}
            </div>
          ) : (
            boundedGroups.map(group => (
              <GroupCard
                key={group.id}
                group={group}
                onClick={cardClickHandler}
                showIntro={viewMode === 'discover'}
                accentColor={groupsColor}
              />
            ))
          )}
        </ModeColumn>
      </div>
    </div>
  );
};

export default GroupsColumn;
